import {
  Modal,
  Stack,
  Text,
  Paper,
  Table,
  Code,
  Group,
  Button,
} from "@mantine/core"
import { useMemo } from "react"
import { t } from "i18next"
import { notifications } from "@mantine/notifications"
import type {
  TheasoftActivity,
  TixlyEvent,
  TixlyTicketSale,
} from "@/@client/Theasoft_ApiServer_ProductionRoyalties"
import { useQueryGet } from "../../../../../typed-client/hooks/use-query-get"
import { usePostMutation } from "../../../../../typed-client/hooks/use-post-mutation"
import { DefaultModal } from "@components/default-modal"

interface MappedEventsModalProps {
  opened: boolean
  onClose: () => void
  onSuccess: () => void
  activity: TheasoftActivity
  tixlyEvent: TixlyEvent
}

const moneyToTwoDecimals = (value: number) => Math.trunc(value * 100) / 100

const formatMoney = (value: number) => moneyToTwoDecimals(value).toFixed(2)

const isMoneyKey = (key: string) => {
  const normalized = key.toLowerCase()
  return (
    normalized.includes("price") ||
    normalized.includes("amount") ||
    normalized.includes("fee") ||
    normalized.includes("tax") ||
    normalized.includes("net") ||
    normalized.includes("gross")
  )
}

const formatValue = (value: unknown, options?: { isMoney?: boolean }) => {
  if (value === null || value === undefined) {
    return "-"
  }

  if (typeof value === "string" || typeof value === "number") {
    if (typeof value === "number" && options?.isMoney) {
      return formatMoney(value)
    }
    return String(value)
  }

  if (typeof value === "boolean") {
    return value ? t("Yes") : t("No")
  }

  return JSON.stringify(value)
}

const getRows = (data: Record<string, unknown>) =>
  Object.entries(data)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => (
      <Table.Tr key={key}>
        <Table.Td>
          <Code>{key}</Code>
        </Table.Td>
        <Table.Td>{formatValue(value, { isMoney: isMoneyKey(key) })}</Table.Td>
      </Table.Tr>
    ))

function MappedEventsModalContent({
  opened,
  onClose,
  onSuccess,
  activity,
  tixlyEvent,
}: MappedEventsModalProps) {
  const { mutateAsync: updateExternalIds, isPending: isUnlinking } =
    usePostMutation("/api/ActicitySale/UpdateExternalIds")
  const { mutateAsync: updateSales, isPending: isUpdatingSales } =
    usePostMutation("/api/ActicitySale/UpdateSals")
  const {
    data: tixlyTickets,
    isLoading: isLoadingTickets,
    error: ticketsError,
  } = useQueryGet(
    "/api/ActicitySale/GetTixlyEventTickets",
    { eventId: tixlyEvent.eventId },
    { enabled: opened }
  )

  const groupedTickets = useMemo(() => {
    const source = (tixlyTickets as TixlyTicketSale[] | undefined) ?? []
    const grouped = new Map<
      string,
      {
        ticketTypeId: number
        ticketTypeName: string
        ticketCount: number
        price: number
        insideFee: number
        fee: number
      }
    >()

    for (const ticket of source) {
      const key = `${ticket.ticketTypeId}-${ticket.ticketTypeName}`
      const existing = grouped.get(key)

      if (existing) {
        existing.ticketCount += ticket.ticketCount
        existing.price += Number(ticket.price)
        existing.insideFee += Number(ticket.insideFee)
        existing.fee += Number(ticket.fee)
        continue
      }

      grouped.set(key, {
        ticketTypeId: ticket.ticketTypeId,
        ticketTypeName: ticket.ticketTypeName,
        ticketCount: ticket.ticketCount,
        price: Number(ticket.price),
        insideFee: Number(ticket.insideFee),
        fee: Number(ticket.fee),
      })
    }

    return Array.from(grouped.values())
  }, [tixlyTickets])

  const handleUnlink = async () => {
    try {
      await updateExternalIds([
        {
          activityId: activity.activityId,
          externalActivityId: null,
        },
      ])

      notifications.show({
        title: t("Success"),
        message: t("UnlinkSuccess"),
        color: "green",
      })

      onSuccess()
      onClose()
    } catch (error) {
      notifications.show({
        title: t("UnlinkFailed"),
        message:
          error instanceof Error ? error.message : t("LinkFailedUnexpected"),
        color: "red",
      })
    }
  }

  const handleUpdateActivitySales = async () => {
    try {
      await updateSales({
        start: activity.date,
        end: activity.date,
        caption: "",
        totalDays: 0,
        isEmpty: false,
        isBaseOrEmpty: false,
      })

      notifications.show({
        title: t("Success"),
        message: t("UpdateSalesSuccess"),
        color: "green",
      })

      onSuccess()
    } catch (error) {
      notifications.show({
        title: t("UpdateSalesFailed"),
        message:
          error instanceof Error ? error.message : t("LinkFailedUnexpected"),
        color: "red",
      })
    }
  }

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Button
          color="teal"
          variant="light"
          onClick={handleUpdateActivitySales}
          loading={isUpdatingSales}
        >
          {t("UpdateSales")}
        </Button>
        <Button
          color="red"
          variant="light"
          onClick={handleUnlink}
          loading={isUnlinking || isUpdatingSales}
          disabled={isUpdatingSales}
        >
          {t("Unlink")}
        </Button>
      </Group>

      <Paper withBorder p="sm">
        <Text fw={600} mb="xs">
          {t("TheasoftActivity")}
        </Text>
        <Table withColumnBorders striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("Field")}</Table.Th>
              <Table.Th>{t("Value")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {getRows(activity as Record<string, unknown>)}
          </Table.Tbody>
        </Table>
      </Paper>

      <Paper withBorder p="sm">
        <Text fw={600} mb="xs">
          {t("TixlyEvent")}
        </Text>
        <Table withColumnBorders striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("Field")}</Table.Th>
              <Table.Th>{t("Value")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {getRows(tixlyEvent as Record<string, unknown>)}
          </Table.Tbody>
        </Table>
      </Paper>

      <Paper withBorder p="sm">
        <Text fw={600} mb="xs">
          {t("TixlyEventTickets")}
        </Text>

        {ticketsError ? (
          <Text c="red" size="sm">
            {t("TicketsCouldNotBeLoaded")}
          </Text>
        ) : isLoadingTickets ? (
          <Text c="dimmed" size="sm">
            {t("LoadingTickets")}
          </Text>
        ) : groupedTickets.length === 0 ? (
          <Text c="dimmed" size="sm">
            {t("NoTicketsFound")}
          </Text>
        ) : (
          <Table withColumnBorders striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("TicketType")}</Table.Th>
                <Table.Th>{t("Count")}</Table.Th>
                <Table.Th>{t("Price")}</Table.Th>
                <Table.Th>{t("InsideFee")}</Table.Th>
                <Table.Th>{t("Fee")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {groupedTickets.map((ticket) => (
                <Table.Tr
                  key={`${ticket.ticketTypeId}-${ticket.ticketTypeName}`}
                >
                  <Table.Td>{ticket.ticketTypeName}</Table.Td>
                  <Table.Td>{formatValue(ticket.ticketCount)}</Table.Td>
                  <Table.Td>
                    {formatValue(ticket.price, { isMoney: true })}
                  </Table.Td>
                  <Table.Td>
                    {formatValue(ticket.insideFee, { isMoney: true })}
                  </Table.Td>
                  <Table.Td>
                    {formatValue(ticket.fee, { isMoney: true })}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  )
}

export function MappedEventsModal(props: MappedEventsModalProps) {
  return (
    <DefaultModal
      opened={props.opened}
      onClose={props.onClose}
      title={t("LinkedData")}
      size="xl"
    >
      <MappedEventsModalContent {...props} />
    </DefaultModal>
  )
}
